import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Power, PowerOff, RefreshCw, Dice5, Spade, Coins, ClipboardList, Sparkles } from 'lucide-react';
import { Switch } from '../../components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * GameAvailabilityPanel — one-tap kill switch for EVERY in-house game.
 *
 * When a game is toggled off, its play endpoints reject and the Lobby hides its
 * tile. The full game list + on/off flags come straight from the server so this
 * panel always covers every game.
 *
 *   GET   /api/admin/games/availability   → { flags:{key:bool}, games:[{key,label,category}] }
 *   PATCH /api/admin/games/availability   → same shape
 */
const CAT_STYLE = {
    Slots: { Icon: Coins, accent: '#FFD700' },
    Keno: { Icon: Sparkles, accent: '#12c9c9' },
    Table: { Icon: Spade, accent: '#39FF14' },
    Sports: { Icon: Dice5, accent: '#a855f7' },
    Lottery: { Icon: ClipboardList, accent: '#ff6fb5' },
    'Majik Bonus': { Icon: Sparkles, accent: '#39FF14' },
};
const CAT_ORDER = ['Majik Bonus', 'Slots', 'Keno', 'Table', 'Sports', 'Lottery'];

export const GameAvailabilityPanel = () => {
    const [flags, setFlags] = useState(null);
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingKey, setSavingKey] = useState(null);

    const apply = (data) => {
        setFlags(data?.flags || {});
        if (Array.isArray(data?.games)) setGames(data.games);
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/games/availability`, { withCredentials: true });
            apply(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load game availability');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggle = async (key, next) => {
        if (savingKey) return;
        setSavingKey(key);
        const prev = flags;
        setFlags((f) => ({ ...f, [key]: next }));
        try {
            const { data } = await axios.patch(
                `${API}/admin/games/availability`,
                { [key]: next },
                { withCredentials: true },
            );
            apply(data);
            const label = games.find((g) => g.key === key)?.label || key;
            toast.success(`${label} ${next ? 'opened' : 'closed'}`);
        } catch (e) {
            setFlags(prev);
            toast.error(e.response?.data?.detail || 'Failed to update');
        } finally {
            setSavingKey(null);
        }
    };

    const openCount = flags ? Object.values(flags).filter(Boolean).length : 0;

    const setAll = async (next) => {
        if (savingKey) return;
        setSavingKey('__all__');
        const body = {};
        games.forEach((g) => { body[g.key] = next; });
        try {
            const { data } = await axios.patch(`${API}/admin/games/availability`, body, { withCredentials: true });
            apply(data);
            toast.success(next ? 'All games opened' : 'All games closed');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to update');
        } finally {
            setSavingKey(null);
        }
    };
    const cats = CAT_ORDER.filter((c) => games.some((g) => g.category === c))
        .concat([...new Set(games.map((g) => g.category))].filter((c) => !CAT_ORDER.includes(c)));

    return (
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0f0f14] to-[#050508] p-4 md:p-5 shadow-[0_0_25px_rgba(0,0,0,0.4)]" data-testid="game-availability-panel">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-[#FFD700]" />
                    <h3 className="text-sm md:text-base font-black tracking-wider text-white">GAMES · AVAILABILITY</h3>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => setAll(true)} disabled={!!savingKey || !games.length}
                        className="text-[10px] font-black px-2 py-1 rounded-md border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-40"
                        data-testid="game-open-all">OPEN ALL</button>
                    <button onClick={() => setAll(false)} disabled={!!savingKey || !games.length}
                        className="text-[10px] font-black px-2 py-1 rounded-md border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-40"
                        data-testid="game-close-all">CLOSE ALL</button>
                    <span className="text-[11px] md:text-xs text-white/60 font-mono">
                        {flags ? `${openCount} / ${games.length} open` : '—'}
                    </span>
                    <button
                        onClick={load}
                        disabled={loading}
                        className="p-1.5 rounded-md hover:bg-white/10 text-white/60 disabled:opacity-40"
                        title="Refresh"
                        data-testid="game-availability-refresh"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            <p className="text-[11px] md:text-xs text-white/50 mb-3 leading-relaxed">
                Flip a switch to instantly close any game. Players can't start a new hand/spin and the tile is hidden from the lobby. In-progress rounds are unaffected.
            </p>

            {cats.map((cat) => {
                const style = CAT_STYLE[cat] || { Icon: Coins, accent: '#FFD700' };
                const Icon = style.Icon;
                const list = games.filter((g) => g.category === cat);
                return (
                    <div key={cat} className="mb-4 last:mb-0">
                        <div className="text-[10px] font-black tracking-widest uppercase text-white/40 mb-2">{cat}</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {list.map(({ key, label }) => {
                                const on = !!flags?.[key];
                                const busy = savingKey === key;
                                return (
                                    <div
                                        key={key}
                                        className={`rounded-xl border px-3 py-3 flex items-center gap-3 transition-all ${on
                                            ? 'border-white/15 bg-white/[0.03]'
                                            : 'border-red-900/40 bg-red-950/20 opacity-80'}`}
                                        data-testid={`game-toggle-${key}`}
                                    >
                                        <div
                                            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                                            style={{
                                                background: on ? `${style.accent}22` : '#2a0808',
                                                border: `1px solid ${on ? style.accent : '#7f1d1d'}55`,
                                            }}
                                        >
                                            <Icon className="w-5 h-5" style={{ color: on ? style.accent : '#7f1d1d' }} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-black text-white tracking-wide truncate">{label}</span>
                                                {on ? (
                                                    <span className="text-[9px] font-mono font-black text-[#39FF14] flex items-center gap-0.5" data-testid={`game-status-${key}`}>
                                                        <Power className="w-3 h-3" /> OPEN
                                                    </span>
                                                ) : (
                                                    <span className="text-[9px] font-mono font-black text-[#FF3B30] flex items-center gap-0.5" data-testid={`game-status-${key}`}>
                                                        <PowerOff className="w-3 h-3" /> CLOSED
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <Switch
                                            checked={on}
                                            onCheckedChange={(next) => toggle(key, next)}
                                            disabled={busy || flags === null}
                                            data-testid={`game-toggle-switch-${key}`}
                                            aria-label={`Toggle ${label}`}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default GameAvailabilityPanel;
